import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  GetUserQuery,
  UserCard_UserFragment,
  Users_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { UserActionCard } from '@modules/_shared/components/UserActionCard';
import { COMPACT_USER_AVATAR_SIZE } from '@modules/_shared/components/UserAvatar';
import { UserCard } from '@modules/_shared/components/UserCard';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { hasValidProtocol } from '@modules/_shared/utils/urlValidation';
import { useCanViewSubscriptions } from '@modules/users/user-overview/hooks/useCanViewSubscriptions';
import { GET_USER, UPDATE_USER_SETTINGS } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';

const GET_USER_CREATOR = gql(/* GraphQL */ `
  query GetUserCreator($id: uuid!) {
    users_by_pk(id: $id) {
      ...UserInfo_user
    }
  }
`);

type UserMetadataCardProps = {
  user: NonNullable<GetUserQuery['users_by_pk']>;
};

const formatDateTime = (locale: string, iso: string | null | undefined) => {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const MarketingValue = ({ value }: { value: string }) =>
  hasValidProtocol(value) ? (
    <MuiLink
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      sx={{
        color: 'text.secondary',
        wordBreak: 'break-all',
        overflowWrap: 'anywhere',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {value}
    </MuiLink>
  ) : (
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        wordBreak: 'break-all',
        overflowWrap: 'anywhere',
      }}
    >
      {value}
    </Typography>
  );

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {label}
    </Typography>
    {typeof value === 'string' ? (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {value}
      </Typography>
    ) : (
      value
    )}
  </Box>
);

type StakeholderRowProps = {
  label: string;
  stakeholder: UserCard_UserFragment | null | undefined;
  isAdmin: boolean;
  emptyLabel: string;
  filters: Users_Bool_Exp;
  onUserChanged: (
    userId: string | null,
    onError: (err: ErrorLike) => void,
  ) => Promise<unknown>;
};

const StakeholderRow = ({
  label,
  stakeholder,
  isAdmin,
  emptyLabel,
  filters,
  onUserChanged,
}: StakeholderRowProps) => (
  <Row
    label={label}
    value={
      isAdmin ? (
        <UserActionCard
          user={stakeholder}
          readOnly={false}
          canDelete
          creatable
          compact
          useLegacyLinks={false}
          editUserInputProps={{ filters }}
          onUserChanged={onUserChanged}
        />
      ) : stakeholder != null ? (
        <Box
          component={Link}
          to={`/users/${stakeholder.id}`}
          sx={{
            display: 'block',
            color: 'inherit',
            textDecoration: 'none',
            '&:hover': { bgcolor: 'action.hover' },
            borderRadius: 1,
          }}
        >
          <UserCard user={stakeholder} condensed compact />
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {emptyLabel}
        </Typography>
      )
    }
  />
);

export const UserMetadataCard = ({ user }: UserMetadataCardProps) => {
  const { t, language } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin ?? false;
  const canViewSubscriptions = useCanViewSubscriptions(user.id);
  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);

  const showAgent = !user.is_broker || user.broker != null;
  const showAccountManager = user.is_broker || user.account_manager != null;

  const { data, loading } = useQuery(GET_USER_CREATOR, {
    variables: { id: user.created_by ?? '' },
    skip: !user.created_by,
  });

  const createdAtFormatted = formatDateTime(language, user.created_at);

  const marketingItems = isAdmin
    ? [
        { label: t('referrer'), value: user.referrer?.trim() || null },
        {
          label: t('referringDomain'),
          value: user.referring_domain?.trim() || null,
        },
        {
          label: t('landingPageUrl'),
          value: user.landing_page_url?.trim() || null,
        },
      ].filter(
        (item): item is { label: string; value: string } => item.value != null,
      )
    : [];

  const [updateUserSettings] = useMutation(UPDATE_USER_SETTINGS, {
    refetchQueries: [
      {
        query: GET_USER,
        variables: { id: user.id, includeSubscriptions: canViewSubscriptions },
      },
    ],
    awaitRefetchQueries: true,
    onError: setApolloError,
  });

  const updateBroker = (
    brokerId: string | null,
    onError: (err: ErrorLike) => void,
  ) =>
    updateUserSettings({
      variables: { id: user.id, user: { broker_id: brokerId } },
      onError,
    });

  const updateAccountManager = (
    accountManagerId: string | null,
    onError: (err: ErrorLike) => void,
  ) =>
    updateUserSettings({
      variables: {
        id: user.id,
        user: { account_manager_id: accountManagerId },
      },
      onError,
    });

  if (
    !createdAtFormatted &&
    !user.created_by &&
    !showAgent &&
    !showAccountManager &&
    marketingItems.length === 0
  ) {
    return (
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />
    );
  }

  return (
    <>
      <ListingAccordion title={t('Metadata')}>
        <Stack sx={{ gap: 2 }}>
          {createdAtFormatted && (
            <Row label={t('Created at')} value={createdAtFormatted} />
          )}
          {marketingItems.map(({ label, value }) => (
            <Row
              key={label}
              label={label}
              value={<MarketingValue value={value} />}
            />
          ))}
          {user.created_by && (
            <Row
              label={t('Created by')}
              value={
                loading ? (
                  <Skeleton variant="text" width={140} />
                ) : data?.users_by_pk ? (
                  <UserInfo
                    user={data.users_by_pk}
                    enableLink
                    showEmail={false}
                    avatarSize={COMPACT_USER_AVATAR_SIZE}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                    -
                  </Typography>
                )
              }
            />
          )}
          {showAgent && (
            <StakeholderRow
              label={t('broker')}
              stakeholder={user.broker}
              isAdmin={isAdmin}
              emptyLabel={t('noAgentAssigned')}
              filters={{ is_broker: { _eq: true } }}
              onUserChanged={updateBroker}
            />
          )}
          {showAccountManager && (
            <StakeholderRow
              label={t('accountManager')}
              stakeholder={user.account_manager}
              isAdmin={isAdmin}
              emptyLabel={t('noAccountManager')}
              filters={{ is_admin: { _eq: true } }}
              onUserChanged={updateAccountManager}
            />
          )}
        </Stack>
      </ListingAccordion>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />
    </>
  );
};
